import { useGetTasksQuery } from '../store/services/tasksApi';
import type { ArtifactReadyState } from '../utils/artifactReadiness';

/** The task panel opens as soon as a plan exists; its status still reflects active work. */
export function useProjectTaskReadiness(projectId: string | null | undefined): ArtifactReadyState {
  const { readiness } = useGetTasksQuery(projectId ?? '', {
    skip: !projectId,
    selectFromResult: ({ currentData }) => {
      const planning = currentData?.filter((task) => task.task_type === 'planning') ?? [];
      let readiness: ArtifactReadyState = currentData?.length ? 'ready' : 'off';

      if (planning.some((task) => ['queued', 'ai_in_progress'].includes(task.status))) readiness = 'generating';
      else if (planning.some((task) => task.status === 'error')) readiness = 'failed';
      else if (planning.some((task) => !['done', 'cancelled', 'invalid'].includes(task.status))) readiness = 'pending';

      return { readiness };
    },
  });

  return readiness;
}

/** Whether a project has any task, including user-created tracking tasks. */
export function useProjectHasTasks(projectId: string | null | undefined): boolean {
  const { hasTasks } = useGetTasksQuery(projectId ?? '', {
    skip: !projectId,
    selectFromResult: ({ currentData }) => ({
      hasTasks: (currentData?.length ?? 0) > 0,
    }),
  });

  return hasTasks;
}

/** Whether automated planning has produced at least one system-owned task. */
export function useProjectHasGeneratedTasks(
  projectId: string | null | undefined,
): boolean {
  const { hasGeneratedTasks } = useGetTasksQuery(projectId ?? '', {
    skip: !projectId,
    selectFromResult: ({ currentData }) => ({
      hasGeneratedTasks: currentData?.some(
        (task) => task.task_type !== 'planning' && task.creation_source === 'system',
      ) ?? false,
    }),
  });

  return hasGeneratedTasks;
}

/** Whether a project has at least one completed task in the shared cache. */
export function useProjectHasCompletedTask(
  projectId: string | null | undefined,
): boolean {
  const { hasCompletedTask } = useGetTasksQuery(projectId ?? '', {
    skip: !projectId,
    selectFromResult: ({ currentData }) => ({
      hasCompletedTask: currentData?.some(
        (task) => task.task_type !== 'planning' && task.status === 'done',
      ) ?? false,
    }),
  });

  return hasCompletedTask;
}
